import { defineStore } from 'pinia';
import { computed, reactive, ref, watch } from 'vue';
import { useGeolocation } from '../adapters/ports.js';
import { getFollows, setFollows } from '../sdk/followStorage.js';
import { isUnderway } from '../sdk/schedule.js';
import { auctionHousesStore } from './auction-houses.store.js';
import { biddingStore } from './bidding.store.js';
import { saleIndexStore } from './sale-index.store.js';

/**
 * Sales-catalog store — backs the two consumer browse-and-follow screens:
 * "My Sales" (sales) and "My Auctions" (auction houses). These replaced the
 * old combined "Sales & auctions" directory + the followed-only "My Sales" /
 * "My Events" screens (Midnight Mint handoff: c-sales-auctions split in two).
 *
 * Both halves are real now. Sales are the live auction events the runtime serves
 * (via the bidding store's grouped lots) plus the sales the schedule holds that
 * the runtime does not serve yet; houses are records
 * from vibe-server, and what each screen used to state as data — a house's
 * distance, whether it is live, and when it next runs — is derived here from
 * the dealer's position and the sales scheduled against it. Follow state is
 * local + optimistic (no network), matching the handoff's follow toggles.
 */

// Lane-type → display chip. Falls back to the raw type for anything new.
const TYPE_LABELS = {
    Simulcast: 'SIMULCAST',
    Timed: 'TIMED ONLINE',
    Sequential: 'SEQUENTIAL',
    automatedAuction: 'AUTOMATED',
    simulcastAuction: 'SIMULCAST',
    timedAuction: 'TIMED ONLINE'
};

// Weekday + clock for a house's next scheduled sale, e.g. 'Tue 9:00 AM'.
const WEEKDAY_SHORT = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const formatNextStart = (isoTimestamp) => {
    const date = new Date(isoTimestamp);
    if (Number.isNaN(date.getTime())) return null;
    let hour = date.getHours();
    const meridiem = hour >= 12 ? 'PM' : 'AM';
    hour = hour % 12 || 12;
    const minutes = String(date.getMinutes()).padStart(2, '0');
    return `${WEEKDAY_SHORT[date.getDay()]} ${hour}:${minutes} ${meridiem}`;
};

export const salesCatalogStore = defineStore('salesCatalogStore', () => {
    const bidding = biddingStore();
    const auctionHouses = auctionHousesStore();
    const saleIndex = saleIndexStore();

    const followedSaleIds = reactive(new Set());
    const followedHouseIds = reactive(new Set());
    const loading = ref(false);
    // Where the dealer is, or null while nobody has said. Null reads as "cannot
    // place this house", which the row renders as a blank distance rather than
    // as a measurement from somewhere they have never been.
    const origin = ref(null);

    // The follows are remembered on this device (sdk/followStorage.js). They are read
    // once, and merged into whatever the dealer has followed in the meantime rather than
    // written over it. Nothing is written until the stored ones have been read, so a
    // refresh can never save an empty set over the dealer's own.
    let followsHydrated = false;
    let hydratingFollows = null;
    function hydrateFollows() {
        if (!hydratingFollows) {
            hydratingFollows = getFollows()
                .then(({ sales: storedSales, houses: storedHouses }) => {
                    storedSales.forEach((id) => followedSaleIds.add(id));
                    storedHouses.forEach((id) => followedHouseIds.add(id));
                })
                // A device that cannot be read starts with no follows rather than failing.
                .catch(() => {})
                .finally(() => { followsHydrated = true; });
        }
        return hydratingFollows;
    }

    watch(
        () => [[...followedSaleIds], [...followedHouseIds]],
        ([storedSales, storedHouses]) => {
            if (!followsHydrated) return;
            void setFollows({ sales: storedSales, houses: storedHouses }).catch(() => {});
        }
    );

    async function load() {
        void hydrateFollows();
        loading.value = true;
        try {
            // Independent reads, settled rather than raced to the first
            // failure: a runtime outage should empty the sales list, not the
            // house list, and a denied location permission should cost the
            // distances rather than the screen. Only `loadLots` can reject —
            // the other three swallow their own errors by construction — and
            // every caller invokes `load()` un-awaited, so a rejection here was
            // previously an unhandled promise rejection rather than anything
            // a screen could act on.
            const outcomes = await Promise.allSettled([
                bidding.loadLots(),
                auctionHouses.ensureLoaded(),
                saleIndex.ensureLoaded(),
                useGeolocation().currentPosition().then((resolved) => { origin.value = resolved ?? null; })
            ]);
            for (const outcome of outcomes) {
                if (outcome.status === 'rejected') {
                    console.warn('Sales catalog load partially failed', outcome.reason);
                }
            }
        } finally {
            loading.value = false;
        }
    }

    // The sales the runtime serves, derived from the live-event groups. A sale is
    // "live" when any of its lanes has a lot currently on the floor (non-scheduled).
    const runtimeSales = computed(() =>
        bidding.allGroups.map((group) => {
            const lots = group.lanes.flatMap((l) => l.lots);
            const live = lots.some((l) => !l.scheduled);
            const laneCount = group.lanes.length;
            return {
                id: group.sale,
                title: group.sale,
                type: group.type,
                typeLabel: TYPE_LABELS[group.type] || String(group.type || '').toUpperCase(),
                live,
                laneCount,
                lotCount: lots.length,
                status: live
                    ? `Live now · ${laneCount} ${laneCount === 1 ? 'lane' : 'lanes'} · ${lots.length} ${lots.length === 1 ? 'asset' : 'assets'}`
                    : `${lots.length} ${lots.length === 1 ? 'asset' : 'assets'} scheduled`
            };
        })
    );

    // The sales the schedule holds that the runtime does not serve yet — a sale on a
    // later day has no event running, so it has no lots to group. They are sales
    // all the same: a calendar of what is coming is made of them. Each is one sale
    // however many lanes it has, never live (the runtime says what is live), and
    // counts its assets from the lanes' run lists.
    const scheduledOnlySales = computed(() => {
        const served = new Set(runtimeSales.value.map((sale) => sale.id));
        const grouped = new Map();
        for (const lane of saleIndex.sales) {
            if (!lane.saleName || served.has(lane.saleName)) continue;
            if (!grouped.has(lane.saleName)) grouped.set(lane.saleName, []);
            grouped.get(lane.saleName).push(lane);
        }
        return [...grouped.entries()].map(([saleName, lanes]) => {
            const lotCount = lanes.reduce((total, lane) => total + (lane.runList?.length ?? 0), 0);
            const type = lanes[0].eventType;
            return {
                id: saleName,
                title: saleName,
                type,
                typeLabel: TYPE_LABELS[type] || String(type || '').toUpperCase(),
                live: false,
                laneCount: lanes.length,
                lotCount,
                status: `${lotCount} ${lotCount === 1 ? 'asset' : 'assets'} scheduled`
            };
        });
    });

    const sales = computed(() => [...runtimeSales.value, ...scheduledOnlySales.value]);

    // Sale documents grouped by the house they run at. A sale document is one
    // LANE, so several rows can share a house — 'Lanes A–D' below is the count
    // of them, not a guess.
    const salesByHouseCode = computed(() => {
        const grouped = new Map();
        for (const sale of saleIndex.sales) {
            const code = sale.auctionHouseCode;
            if (!code) continue;
            if (!grouped.has(code)) grouped.set(code, []);
            grouped.get(code).push(sale);
        }
        return grouped;
    });

    // The houses each sale runs at, by sale name. A sale is several lanes, and the
    // lanes of one sale can sit at more than one house, so this is a set.
    const houseCodesBySaleName = computed(() => {
        const grouped = new Map();
        for (const lane of saleIndex.sales) {
            if (!lane.saleName || !lane.auctionHouseCode) continue;
            if (!grouped.has(lane.saleName)) grouped.set(lane.saleName, new Set());
            grouped.get(lane.saleName).add(lane.auctionHouseCode);
        }
        return grouped;
    });

    // The names of the sales the runtime currently reports as running, so a
    // house's liveness comes from the same source the sales list reads rather
    // than from a second opinion.
    const liveSaleNames = computed(
        () => new Set(sales.value.filter((sale) => sale.live).map((sale) => sale.title))
    );

    /**
     * A house, with the three things the browse row states about it derived
     * rather than stored: how far away it is, whether it is running anything,
     * and when it next does.
     */
    const houses = computed(() =>
        auctionHouses.houses.map((house) => {
            const scheduled = salesByHouseCode.value.get(house.code) ?? [];
            const laneCount = scheduled.length;
            // The runtime's answer when it has one, the schedule's otherwise.
            //
            // The schedule half is not a nicety: `startAt: null` means RUNNING
            // NOW, and the earlier version filtered those out before looking for
            // the earliest start — so a house holding three live lanes reported
            // that it had nothing scheduled, which is what the screenshots
            // caught.
            const live = liveSaleNames.value.size
                ? scheduled.some((sale) => liveSaleNames.value.has(sale.saleName))
                : scheduled.some((sale) => isUnderway(sale.startAt));
            const nextStart = scheduled
                .map((sale) => sale.startAt)
                .filter(Boolean)
                .sort()[0] ?? null;
            // The sale the row's last line is about, so the row can open it:
            // the one running there now, else the one that starts next. Null
            // for a house with nothing scheduled, which then has nothing to
            // open. Lanes of one sale share its name, so any live lane names it.
            const liveSale = live
                ? scheduled.find((sale) => (liveSaleNames.value.size
                    ? liveSaleNames.value.has(sale.saleName)
                    : isUnderway(sale.startAt)))
                : null;
            const nextSale = nextStart ? scheduled.find((sale) => sale.startAt === nextStart) : null;
            const saleId = (liveSale ?? nextSale)?.saleName ?? null;
            const distanceMiles = auctionHouses.distanceFrom(house.code, origin.value);
            return {
                id: house.code,
                name: house.name,
                loc: [house.city, house.state].filter(Boolean).join(', '),
                distanceMiles,
                // An un-placeable house reads as blank rather than as
                // 'Infinity mi'. It can happen — a house seeded without
                // coordinates — and a blank row is honest where a number is not.
                dist: Number.isFinite(distanceMiles) ? `${Math.round(distanceMiles)} mi` : null,
                live,
                saleId,
                // 'No sales scheduled' is reachable only when the house really
                // holds none: a lane with a null start reads live above, and a
                // lane with a future start has a nextStart to name.
                when: live
                    ? `Live now · ${laneCount} ${laneCount === 1 ? 'lane' : 'lanes'}`
                    : (nextStart ? formatNextStart(nextStart) : 'No sales scheduled')
            };
        })
    );

    const isSaleFollowed = (id) => followedSaleIds.has(id);
    const isHouseFollowed = (id) => followedHouseIds.has(id);
    // A sale "in My Auctions" runs, on any lane, at a house the dealer follows.
    const isSaleInFollowedHouse = (id) => {
        for (const code of houseCodesBySaleName.value.get(id) ?? []) {
            if (followedHouseIds.has(code)) return true;
        }
        return false;
    };

    // Does the dealer follow anything at all — a sale or a house? Home's sections read
    // it to tell "you follow nothing yet" from "nothing of yours is in this window".
    const hasFollows = computed(() => followedSaleIds.size > 0 || followedHouseIds.size > 0);

    function toggleSale(id) {
        if (followedSaleIds.has(id)) followedSaleIds.delete(id);
        else followedSaleIds.add(id);
    }
    function toggleHouse(id) {
        if (followedHouseIds.has(id)) followedHouseIds.delete(id);
        else followedHouseIds.add(id);
    }

    // "My Auctions" browse list — live first, then nearest. Compared rather
    // than subtracted: two un-placeable houses are both Infinity, and
    // `Infinity - Infinity` is NaN, which makes the comparator inconsistent and
    // the order arbitrary. This sorts them last and keeps them stable.
    const auctions = computed(() =>
        [...houses.value].sort(
            (a, b) =>
                Number(b.live) - Number(a.live) ||
                (a.distanceMiles === b.distanceMiles ? 0 : (a.distanceMiles < b.distanceMiles ? -1 : 1))
        )
    );

    return {
        loading,
        sales, houses, auctions,
        isSaleFollowed, isHouseFollowed, isSaleInFollowedHouse, hasFollows, toggleSale, toggleHouse,
        load
    };
});
